import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { SelectionList } from './userOffice.types';

type Day = { id: number; label: string; value: number };

interface Props {
  /* -------- TIME -------- */
  startTime: string;
  duration: string;
  endTime: string;

  onStartTimeChange: (v: string) => void;
  onDurationChange: (v: string) => void;

  /* -------- EXAM ROOM -------- */
  examRoomId?: number | '';
  examRooms?: SelectionList[];
  onExamRoomChange?: (id: number) => void;

  /* -------- WEEK DAYS -------- */
  daysOfWeek: Day[];
  selectedWeekDays: number[];
  onWeekDayToggle: (day: number) => void;

  /* -------- VALIDATION -------- */
  submitAttempted?: boolean;
}

export default function OfficeSetting({
  startTime,
  duration,
  endTime,
  onStartTimeChange,
  onDurationChange,

  examRoomId,
  examRooms = [],
  onExamRoomChange,

  daysOfWeek,
  selectedWeekDays,
  onWeekDayToggle,

  submitAttempted = false,
}: Props) {
  /* -------- AUTO CALCULATE END TIME -------- */
  const calculatedEndTime =
    startTime && duration
      ? dayjs(startTime, 'HH:mm')
          .add(Number(duration || 0), 'minute')
          .format('HH:mm')
      : '';

  return (
    <Box>
      <Grid container spacing={3}>
        {/* ---------------- EXAM ROOM ---------------- */}
        {examRooms.length > 0 && (
          <Grid item xs={12}>
            <InputLabel required>Exam Room</InputLabel>
            <TextField
              select
              fullWidth
              value={examRoomId ?? ''}
              error={submitAttempted && !examRoomId}
              helperText={submitAttempted && !examRoomId ? 'Exam Room is required' : ''}
              onChange={(e) => onExamRoomChange && onExamRoomChange(Number(e.target.value))}
            >
              <MenuItem value='' disabled>
                Select Exam Room
              </MenuItem>

              {examRooms.map((room) => (
                <MenuItem key={room.id} value={room.id}>
                  {room.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {/* ---------------- START TIME ---------------- */}
        <Grid item xs={4}>
          <InputLabel required>Start Time</InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <TimePicker
              value={startTime ? dayjs(startTime, 'HH:mm') : null}
              onChange={(v) => onStartTimeChange(v ? v.format('HH:mm') : '')}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: submitAttempted && !startTime,
                  helperText: submitAttempted && !startTime ? 'Start Time is required' : '',
                },
              }}
            />
          </LocalizationProvider>
        </Grid>

        {/* ---------------- DURATION ---------------- */}
        <Grid item xs={4}>
          <InputLabel required>Duration (mins)</InputLabel>
          <TextField
            type='number'
            fullWidth
            value={duration}
            error={submitAttempted && !duration}
            helperText={submitAttempted && !duration ? 'Duration is required' : ''}
            inputProps={{ min: 1 }}
            onChange={(e) => {
              const value = e.target.value;
              if (Number(value) >= 0) {
                onDurationChange(value);
              }
            }}
          />
        </Grid>

        {/* ---------------- END TIME ---------------- */}
        <Grid item xs={4}>
          <InputLabel>End Time</InputLabel>
          <TextField fullWidth disabled value={calculatedEndTime || endTime} />
        </Grid>

        {/* ---------------- WEEK DAYS ---------------- */}
        <Grid item xs={12}>
          <InputLabel required>Available Days</InputLabel>

          <FormGroup row>
            {daysOfWeek.map((d) => (
              <FormControlLabel
                key={d.id}
                control={
                  <Checkbox
                    checked={selectedWeekDays.includes(d.value)}
                    onChange={() => onWeekDayToggle(d.value)}
                  />
                }
                label={d.label}
              />
            ))}
          </FormGroup>

          {submitAttempted && selectedWeekDays.length === 0 && (
            <Typography color='error' variant='caption'>
              At least one day must be selected
            </Typography>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
